import React, { useState } from 'react';
import { useLocation, useNavigate, NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useMainNavItems } from '../hooks/useMainNavItems';
import {
  Menu,
  LogOut,
  Settings as SettingsIcon,
  LifeBuoy,
  Crown,
  HelpCircle,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';

/**
 * Mobile-only top bar (hidden on md+).
 *
 * The hamburger opens a left-side Sheet with the full nav. The
 * PRIMARY items list is shared with the desktop `SideNav` via the
 * `useMainNavItems` hook so the two surfaces cannot drift apart
 * again. Secondary items mirror what the desktop exposes inside
 * its "More" dropdown (Settings, Help, Contact, Legal, Logout).
 *
 * All labels + page titles resolve from the `header` i18n
 * namespace — never hardcode a language string here.
 */
const TopBar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('header');
  // Shared source of truth with `SideNav` desktop — see
  // `hooks/useMainNavItems.js`.
  const items = useMainNavItems();

  if (['/login', '/register'].includes(location.pathname)) return null;
  if (location.pathname.startsWith('/checkout/')) return null;
  if (!user) return null;

  // Page title map — the Wallet route always shows the localised
  // "Wallet" / "Portafoglio" title (no more "Creator Dashboard"
  // swap that broke IT localisation).
  const titleMap = {
    '/': t('mobile.titles.home'),
    '/explore': t('mobile.titles.explore'),
    '/lives': t('mobile.titles.lives'),
    '/create': t('mobile.titles.create'),
    '/wallet': t('mobile.titles.wallet'),
    '/admin': t('mobile.titles.admin'),
    '/settings': t('mobile.titles.settings'),
    '/profile': user?.username || t('mobile.titles.profile'),
  };
  const title = titleMap[location.pathname] || t('mobile.titles.home');
  const isHome = location.pathname === '/';

  // Secondary items — parity with `SideNav`'s "More" DropdownMenu.
  // Phase 44 · Feb 2026 — FAQ, Contatti, Termini, Privacy e Cookie
  // rimossi qui e nel SideNav desktop. Restano nel footer legale.
  const secondaryItems = [
    { to: '/settings', icon: SettingsIcon, label: t('menu.settings'), key: 'settings' },
  ];

  // Phase 51 — Assistenza is not a route: it opens the floating
  // SupportChatWidget via a global custom event.
  const openSupportChat = () => {
    setOpen(false);
    // Small delay so the sheet close animation doesn't collide with
    // the widget's own open animation on lower-end phones.
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('nymia:open-support-chat'));
    }, 120);
  };

  const onLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-card/95 px-3 backdrop-blur md:hidden"
      data-testid="mobile-topbar"
    >
      <div className="flex items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              aria-label={t('mobile.open_menu')}
              className="flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-secondary"
              data-testid="mobile-menu-trigger"
            >
              <Menu className="h-6 w-6" strokeWidth={1.9} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="flex w-[min(78vw,272px)] flex-col border-r border-border bg-card p-0 [&>button.absolute]:text-foreground"
            style={{
              height: '100dvh',
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            data-testid="mobile-menu-sheet"
          >
            <SheetHeader className="shrink-0 border-b border-border px-5 pb-4 pt-5">
              <SheetTitle className="flex items-center gap-2 nymia-logo text-2xl">
                Nymia
                {user.is_verified_creator && (
                  <Crown className="h-4 w-4 gold-text" data-testid="mobile-menu-vip-crown" />
                )}
              </SheetTitle>
              <p className="text-left text-xs text-muted-foreground">@{user.username}</p>
              <div className="mt-3">
                <LanguageSwitcher variant="full" align="start" testId="mobile-language-switcher" />
              </div>
            </SheetHeader>

            <nav
              className="flex flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-3 py-4"
              data-testid="mobile-menu-scroll"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {/* Primary items — shared 1:1 with desktop SideNav */}
              {items.map(({ to, icon: Icon, key, end }) => {
                const label = t(`nav.${key}`);
                return (
                  <SheetClose asChild key={key}>
                    <NavLink
                      to={to}
                      end={end}
                      data-testid={`mobile-menu-link-${key}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-secondary ${
                          isActive ? 'gold-text font-semibold bg-secondary/60' : 'text-foreground'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2.3 : 1.8} />
                          <span>{label}</span>
                        </>
                      )}
                    </NavLink>
                  </SheetClose>
                );
              })}

              {/* Secondary items — parity with desktop "More" dropdown */}
              <div className="my-2 border-t border-border" />
              {secondaryItems.map(({ to, icon: Icon, label, key }) => (
                <SheetClose asChild key={key}>
                  <NavLink
                    to={to}
                    data-testid={`mobile-menu-link-${key}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium transition-colors hover:bg-secondary ${
                        isActive ? 'gold-text font-semibold bg-secondary/60' : 'text-muted-foreground'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                    <span>{label}</span>
                  </NavLink>
                </SheetClose>
              ))}
              {/* Phase 51 · Assistenza — opens the floating support chat */}
              <button
                onClick={openSupportChat}
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                data-testid="mobile-menu-link-support"
              >
                <LifeBuoy className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                <span>{t('menu.support', 'Assistenza')}</span>
              </button>

              <div className="my-2 border-t border-border" />
              <button
                onClick={onLogout}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10"
                data-testid="mobile-menu-logout"
              >
                <LogOut className="h-5 w-5 shrink-0" strokeWidth={1.8} />
                <span>{t('menu.logout')}</span>
              </button>
              {/* Tail spacer so the last item never sticks to the safe-area edge */}
              <div className="pb-4" />
            </nav>
          </SheetContent>
        </Sheet>

        {isHome ? (
          <Link to="/" className="text-2xl nymia-logo">Nymia</Link>
        ) : (
          <span className="text-base font-semibold">{title}</span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Link
          to="/help"
          aria-label={t('menu.help', 'Aiuto')}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-secondary"
          data-testid="topbar-help-btn"
        >
          <HelpCircle className="h-[22px] w-[22px]" strokeWidth={1.9} />
        </Link>
        <NotificationBell />
      </div>
    </header>
  );
};

export default TopBar;
